'use client';

import firebase from 'firebase/compat/app';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { db } from '../config/firebaseConfig';
import { UserAuth } from '../context/authContext';
import DashboardLayout from './layout/DashboardLayout';
import PrismCard from './PrismCard';
import CopyComponent from './utils/CopyComponent';

export default function Participants() {
  const { userData, user }=UserAuth();
  const [roomId, setRoomId]=useState(localStorage.getItem('roomId')||'');
  const [datax, setDatax]=useState('');
  const [loading, setLoading]=useState(false);
  const [createdBy, setCreatedBy]=useState([]);

  useEffect(() => {
    if (!roomId) return;

    const unsubscribe=db
      .collection('rooms')
      .doc(roomId)
      .onSnapshot(
        (roomSnapshot) => {
          if (roomSnapshot.exists) {
            const data=roomSnapshot.data();
            setDatax(data);
            console.log('Fetched room data:', data);
            setCreatedBy(data.createdBy||[]);
          } else {
            console.log('No data received for room, invalid ID.');
            localStorage.removeItem('roomId');
            window.location.reload();
          }
        },
        (error) => {
          console.error('Error fetching room details:', error);
          toast.error('Error fetching room details.');
        }
      );

    return () => unsubscribe();
  }, [roomId]);

  const [role, setRole]=useState(() => localStorage.getItem('role')||'');

  const handleLeaveRoom=async () => {
    try {
      await db
        .collection('rooms')
        .doc(roomId)
        .update({
          [`${role}s`]: firebase.firestore.FieldValue.arrayRemove(
            userData.publicKey
          ),
        });
      localStorage.removeItem('roomId');
      localStorage.removeItem('role');
      toast.success('You have left the room successfully.');
      window.location.href='/';
      setRoomId('');
      setRole('');
    } catch (error) {
      console.error('Error leaving room:', error);
      toast.error('Failed to leave room. Please try again later.');
    }
  };

  const handleEndRoom=async () => {
    if (roomId&&user.email) {
      try {
        const roomDocRef=db.collection('rooms').doc(roomId);

        const subcollectionNames=['blocks', 'transactions'];

        await Promise.all(
          subcollectionNames.map(async (subcollectionName) => {
            const subcollectionRef=roomDocRef.collection(subcollectionName);
            const docs=await subcollectionRef.get();
            docs.forEach(async (doc) => {
              await doc.ref.delete();
            });
          })
        );

        await roomDocRef.delete();

        toast.success('Room ended successfully.');
        window.location.href='/';

        setRoomId('');
        localStorage.removeItem('roomId');
      } catch (error) {
        console.error('Error ending room:', error);
        toast.error('Failed to end room. Please try again later.');
      }
    } else {
      toast.error('Cannot end room: Room ID or user information is missing.');
    }
  };

  if (!roomId) {
    return (
      <DashboardLayout>
        <section className="min-h-screen pt-10 pb-8 px-6">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-2xl font-semibold text-white mb-6">Lobby</h1>
            <PrismCard className="bg-white/5 backdrop-blur-xl rounded-xl p-12 border border-white/10 shadow-2xl shadow-purple-500/10 before:absolute before:inset-0 before:rounded-xl before:bg-gradient-to-br before:from-white/10 before:to-transparent before:opacity-50 before:pointer-events-none">
              <div className="relative z-10 text-center">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill="white"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-white mb-2">
                  Join a Room First
                </h2>
                <p className="text-white/60">
                  You need to be in a room to view participants
                </p>
              </div>
            </PrismCard>
          </div>
        </section>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <section className="min-h-screen pt-10 pb-8 px-6">
        <div className="max-w-7xl mx-auto text-white">
          <h1 className="text-2xl font-bold text-white mb-6">Lobby</h1>

          <div className="max-w-2xl">
            <ListofParticipant roomId={roomId} />
            <ListMiner roomId={roomId} />
          </div>
        </div>
      </section>
    </DashboardLayout>
  );
}

const ListofParticipant=({ roomId }) => {
  const [participants, setParticipants]=useState([]);

  useEffect(() => {
    if (!roomId) return;

    const unsubscribe=db
      .collection('rooms')
      .doc(roomId)
      .onSnapshot(async (doc) => {
        if (doc.exists) {
          const roomData=doc.data();
          if (roomData.participants&&roomData.participants.length>0) {
            const users=await getDocs(
              query(
                collection(db, 'users'),
                where('publicKey', 'in', roomData.participants)
              )
            );
            setParticipants(users.docs.map((v) => v.data()));
          } else {
            setParticipants([]);
          }
        }
      });

    return () => unsubscribe();
  }, [roomId]);

  return (
    <div className="bg-[#343B4F]/60 backdrop-blur rounded-xl p-5 border border-white/10 mb-4">
      <h4 className="text-white/80 text-base font-normal mb-4">
        List of Participants
      </h4>
      <div className="space-y-3">
        {participants.length===0? (
          <p className="text-white/40 text-sm">No participants</p>
        ):(
          participants.map((participant, index) => (
            <div key={index} className="flex items-start gap-2">
              <div className="flex-1 min-w-0 ">
                <div className="flex items-center gap-2 mb-1 cursor-pointer">
                  <span className="text-white  px-2 py-1 rounded text-xs font-mono">
                    {participant.email}
                  </span>
                  <CopyComponent data={participant.publicKey} />
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const ListMiner=({ roomId }) => {
  const [miners, setMiners]=useState([]);

  useEffect(() => {
    if (!roomId) return;

    const unsubscribe=db
      .collection('rooms')
      .doc(roomId)
      .onSnapshot(async (doc) => {
        if (doc.exists) {
          const roomData=doc.data();

          if (roomData.miners&&roomData.miners.length>0) {
            const users=await getDocs(
              query(
                collection(db, 'users'),
                where('publicKey', 'in', roomData.miners)
              )
            );

            setMiners(users.docs.map((v) => v.data()));
          } else {
            setMiners([]);
          }
        }
      });

    return () => unsubscribe();
  }, [roomId]);

  return (
    <div className="bg-[#343B4F]/60 backdrop-blur rounded-xl p-5 border border-white/10">
      <h4 className="text-white/80 text-base font-normal mb-4">
        List of Miners
      </h4>
      <div className="space-y-3">
        {miners.length===0? (
          <p className="text-white/40 text-sm">No miners</p>
        ):(
          miners.map((participant, index) => (
            <div key={index} className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 cursor-pointer">
                  <span className="text-white  px-2 py-1 rounded text-xs font-mono">
                    {participant.email}
                  </span>
                  <CopyComponent data={participant.publicKey} />
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
